import { Navigate } from 'react-router-dom';
import { lazy } from 'react';

// Every route component is loaded on demand. These were static imports, which
// put all ~170 pages into the entry chunk - 17.3MB raw / 4.5MB gzipped that a
// user downloaded and parsed before the first screen could paint. They render
// inside the <Suspense fallback={<Loader/>}> in App.js, so the loader already
// covers the fetch.

// Lazy load heavy components
const Dashboard = lazy(() => import("../pages/Dashboard/Dashboard"));
const AuditorsDashboard = lazy(() => import("../pages/Dashboard/AuditorsDashboard"));
const LogsHub = lazy(() => import("../pages/Logs/LogsHub"));
const Governance = lazy(() => import("../pages/Governance/Governance"));
const ReportsCenter = lazy(() => import("../pages/Reports/ReportsCenter"));
const ForgetPassword = lazy(() => import("../pages/Login/ForgetPassword"));
const FindOomeroId = lazy(() => import("../pages/FindOomeroId/FindOomeroId"));
const WorkflowBuilder = lazy(() => import("../pages/WorkflowBuilder/WorkflowBuilder"));
const Login = lazy(() => import("../pages/Login/Login"));
const ResetPassword = lazy(() => import("../pages/Login/ResetPassword"));
const Reigistration = lazy(() => import("../pages/Registration/Reigistration"));
const Screening = lazy(() => import("../pages/Screening/Screening"));
const CarCreateProfiled = lazy(() => import("../pages/Profile/CreateProfile"));
const IndividualPersonalDetails = lazy(() => import("../pages/Profile/Individual/IndividualPersonalDetails"));
const IndividualAddress = lazy(() => import("../pages/Profile/Individual/IndividualAddress"));
const IndividualBankDetails = lazy(() => import("../pages/Profile/Individual/IndividualBankDetails"));
const IndividualKYCVerification = lazy(() => import("../pages/Profile/Individual/IndividualKYCVerification"));
const IndividualKYCWaiting = lazy(() => import("../pages/Profile/Individual/IndividualKYCWaiting"));
const IndividualAppicationDone = lazy(() => import("../pages/Profile/Individual/IndividualAppicationDone"));
const IndividualWebWelcome = lazy(() => import("../pages/Webform/Individual/WelcomeScreen"));
const IndividualWebPersonalDetails = lazy(() => import("../pages/Webform/Individual/WebformPersonalDetails"));
const IndividualWebAddress = lazy(() => import("../pages/Webform/Individual/WebformAddress"));
const IndividualWebBankDetail = lazy(() => import("../pages/Webform/Individual/WebformBankDetails"));
const IndividualWebKYCVerification = lazy(() => import("../pages/Webform/Individual/WebformKYCVerification"));
const IndividualWebApplication = lazy(() => import("../pages/Webform/Individual/WebformAppicationDone"));
const FaceVerification = lazy(() => import("../pages/Verification/FaceVerification"));
const ComprehensiveReport = lazy(() => import("../pages/ComprehensiveReport/ComprehensiveReport"));
const CorporatePersonalDetails = lazy(() => import("../pages/Profile/Corporate/CorporatePersonalDetails"));
const Tax = lazy(() => import("../pages/Tax/Tax"));
const Addtax = lazy(() => import("../pages/Tax/Addtax"));
const CompanyTaxMapping = lazy(() => import("../pages/Tax/CompanyTaxMapping"));
const TaxOverview = lazy(() => import("../pages/Tax/TaxOverview"));
const FxRates = lazy(() => import("../pages/FxRates/FxRates"));
const AddNewFxRates = lazy(() => import("../pages/FxRates/AddNewFxRates"));
const CorporateInformation = lazy(() => import("../pages/Profile/Corporate/CorporateInformation"));
const CorporateCompliance = lazy(() => import("../pages/Profile/Corporate/CorporateCompliance"));
const CorporateBankDetails = lazy(() => import("../pages/Profile/Corporate/CorporateBankDetails"));
const CorporateDirectorList = lazy(() => import("../pages/Profile/Corporate/Directors/CorporateDirectorList"));
const UsersRole = lazy(() => import("../pages/UsersRoles/UsersRole"));
const AddNewUserRoles = lazy(() => import("../pages/UsersRoles/AddNewUserRoles"));
const AddDirectorPersonlDetails = lazy(() => import("../pages/Profile/Corporate/Directors/AddDirectorPersonlDetails"));
const AddDirectorAddress = lazy(() => import("../pages/Profile/Corporate/Directors/AddDirectorAddress"));
const AddDirectorBankDetails = lazy(() => import("../pages/Profile/Corporate/Directors/AddDirectorBankDetails"));
const CorporateShareholderList = lazy(() => import("../pages/Profile/Corporate/ShareHolders/CorporateShareholderList"));
const AddShareholdePersonalDetails = lazy(() => import("../pages/Profile/Corporate/ShareHolders/AddShareholdePersonalDetails"));
const AddShareholdeAddress = lazy(() => import("../pages/Profile/Corporate/ShareHolders/AddShareholdeAddress"));
const AddShareholderBankDetails = lazy(() => import("../pages/Profile/Corporate/ShareHolders/AddShareholderBankDetails"));
const CorporateMlroDetails = lazy(() => import("../pages/Profile/Corporate/CorporateMlroDetails"));
const CorporateMembersList = lazy(() => import("../pages/Profile/Corporate/CorporateMembersList"));
const CorporateApplicationDone = lazy(() => import("../pages/Profile/Corporate/CorporateApplicationDone"));
const ManageClients = lazy(() => import("../pages/ManageClients/ManageClients"));
const NewClientSetup = lazy(() => import("../pages/ManageClients/NewClientSetup"));
const ClientServiceSetup = lazy(() => import("../pages/ManageClients/ClientServiceSetup"));
const ManageAdmin = lazy(() => import("../pages/ManageClients/ManageAdmin"));
const ClientBiling = lazy(() => import("../pages/ViewClientBilling/ClientBiling"));
const ViewClientBilling = lazy(() => import("../pages/ViewClientBilling/ViewClientBilling"));
const PreviousBills = lazy(() => import("../pages/ViewClientBilling/PreviousBills"));
const EditPricing = lazy(() => import("../pages/ViewClientBilling/EditPricing"));
const SearchAmlCheck = lazy(() => import("../pages/AmlCheck/SearchAmlCheck"));
const ResultsSearch = lazy(() => import("../pages/AmlCheck/ResultsSearch"));
const SingleResultsData = lazy(() => import("../pages/AmlCheck/SingleResultsData"));
const ManageUsers = lazy(() => import("../pages/ManageUsers/ManageUsers"));
const ListofOomeroUsers = lazy(() => import("../pages/ManageUsers/ListofOomeroUsers"));
const AddNewUsers = lazy(() => import("../pages/ManageUsers/AddNewUsers"));
const CorporateScreening = lazy(() => import("../pages/Screening/CorporateScreening"));
const StartScreening = lazy(() => import("../pages/Screening/StartScreening"));
const ScreeningCompanyDetails = lazy(() => import("../pages/Screening/ScreeningCompanyDetails"));
const CompanyShareholderScreeningDetails = lazy(() => import("../pages/Screening/CompanyShareholderScreeningDetails"));
const CompanyCheck = lazy(() => import("../pages/CompanyCheck/CompanyCheck"));
const PreviouslyReports = lazy(() => import("../pages/CompanyCheck/PreviouslyReports"));
const IndividualScreening = lazy(() => import("../pages/IndividualScreening/IndividualScreening"));
const IndividualScreeningDetails = lazy(() => import("../pages/IndividualScreening/IndividualScreeningDetails"));
const Pricing = lazy(() => import("../pages/Pricing/Pricing"));
const IndividualViewedScreening = lazy(() => import("../pages/ViewScreeningDetails/individual/individualViewedScreening"));
const ViewAllCorporateList = lazy(() => import("../pages/ViewScreeningDetails/ViewAllCorporateList"));
const ViewAllIndividualList = lazy(() => import("../pages/ViewScreeningDetails/ViewAllIndividualList"));
const Invoices = lazy(() => import("../pages/MyAccount/Invoices"));
const ActivePlan = lazy(() => import("../pages/MyAccount/ActivePlan"));
const MyProfile = lazy(() => import("../pages/MyAccount/MyProfile"));
const ClientRekycTime = lazy(() => import("../pages/Settings/ClientRekycTime"));
const ClientBook = lazy(() => import("../pages/ClientBook/ClientBook"));
const KypCustomerChecklist = lazy(() => import("../pages/KypCustomerChecklist/KypCustomerChecklist"));
const ClientReviewForm = lazy(() => import("../pages/ClientReview/ClientReviewForm"));
const ChecklistUpload = lazy(() => import("../pages/ChecklistUpload/ChecklistUpload"));
const SupplyChainForm = lazy(() => import("../pages/SupplyChain/SupplyChainForm"));
const ShipmentUpload = lazy(() => import("../pages/ShipmentUpload/ShipmentUpload"));
// Refinery workspace: every refinery feature in its own section, apart from screening.
const RefineryHome = lazy(() => import("../pages/Refinery/RefineryHome"));
const RefineryClients = lazy(() => import("../pages/Refinery/RefineryClients"));
const RefineryOnboardings = lazy(() => import("../pages/Refinery/RefineryOnboardings"));
const RefineryShipments = lazy(() => import("../pages/Refinery/RefineryShipments"));
const RefineryCompany = lazy(() => import("../pages/Refinery/RefineryCompany"));
const RefineryIndividual = lazy(() => import("../pages/Refinery/RefineryIndividual"));
const RefinerySettings = lazy(() => import("../pages/Refinery/RefinerySettings"));
const RefineryOnboardingPublic = lazy(() => import("../pages/RefineryOnboardingPublic/RefineryOnboardingPublic"));
const RefineryOnboard = lazy(() => import("../pages/Refinery/RefineryOnboard"));
const RefineryHomeGate = lazy(() => import("../pages/Refinery/RefineryHomeGate"));
function DashboardHome() { return <RefineryHomeGate component={Dashboard} />; }
// Oomero Asset IDs: become an issuer, add IDs to gold bars and other assets, and a public check.
const AssetsHome = lazy(() => import("../pages/Assets/AssetsHome"));
const AssetNew = lazy(() => import("../pages/Assets/AssetNew"));
const AssetDetail = lazy(() => import("../pages/Assets/AssetDetail"));
const AssetIssuer = lazy(() => import("../pages/Assets/AssetIssuer"));
const AssetIssuerReviews = lazy(() => import("../pages/Assets/AssetIssuerReviews"));
const VerifyAsset = lazy(() => import("../pages/Assets/VerifyAsset"));
// The Oomero Asset ID register: an account's own valuables, its plan, buying and selling (13 Oct 2026).
const RegisterHome = lazy(() => import("../pages/Assets/register/RegisterHome"));
const RegisterForm = lazy(() => import("../pages/Assets/register/RegisterForm"));
const RegisterItem = lazy(() => import("../pages/Assets/register/RegisterItem"));
const RegisterSales = lazy(() => import("../pages/Assets/register/RegisterSales"));
const RegisterPlan = lazy(() => import("../pages/Assets/register/RegisterPlan"));
const RegisterPrices = lazy(() => import("../pages/Assets/register/RegisterPrices"));
const ASSET_PERMISSIONS = ["create_asset_onboarding"];
const REFINERY_PERMISSIONS = ["view_corporate_screening", "view_onboarded_corporate_profiles", "view_onboarded_individual_profiles", "view_individual_screening_list", "manage_client_rekyc_settings"];
// The refinery settings used to be five items under Settings; old links open the matching area.
const refinerySettingsRedirect = (area) => function RefinerySettingsRedirect() { return <Navigate to={`/refinery/settings?area=${area}`} replace />; };
const ComplianceRiskSettings = lazy(() => import("../pages/Settings/ComplianceRiskSettings"));
const RiskWeightageSettings = lazy(() => import("../pages/Settings/RiskWeightageSettings"));
const UboThreshold = lazy(() => import("../pages/Settings/UboThreshold"));
const SelfServicePlan = lazy(() => import("../pages/MyAccount/SelfServicePlan"));
const GoogleAuthenticator = lazy(() => import("../pages/MyAccount/GoogleAuthenticator"));
const ChangePassword = lazy(() => import("../pages/MyAccount/ChangePassword"));
const WelcomeScreen = lazy(() => import("../pages/Webform/Individual/WelcomeScreen"));
const WebformPersonalDetails = lazy(() => import("../pages/Webform/Individual/WebformPersonalDetails"));
const WebformAddress = lazy(() => import("../pages/Webform/Individual/WebformAddress"));
const WebformBankDetails = lazy(() => import("../pages/Webform/Individual/WebformBankDetails"));
const WebformKYCVerification = lazy(() => import("../pages/Webform/Individual/WebformKYCVerification"));
const WebformAppicationDone = lazy(() => import("../pages/Webform/Individual/WebformAppicationDone"));
const CorporateWelcomeScreen = lazy(() => import("../pages/Webform/Corporate/CorporateWelcomeScreen"));
const CorporateWebformInformation = lazy(() => import("../pages/Webform/Corporate/CorporateWebformInformation"));
const CorporateWebformCompliance = lazy(() => import("../pages/Webform/Corporate/CorporateWebformCompliance"));
const CorporateWebformMlroDetails = lazy(() => import("../pages/Webform/Corporate/CorporateWebformMlroDetails"));
const CorporateWebformBankDetails = lazy(() => import("../pages/Webform/Corporate/CorporateWebformBankDetails"));
const CorporateWebformDirectorList = lazy(() => import("../pages/Webform/Corporate/CorporateWebformDirectorList"));
const WebformAddDirectorPersonlDetails = lazy(() => import("../pages/Webform/Corporate/WebformAddDirectorPersonlDetails"));
const CorporateAddDirectorAddress = lazy(() => import("../pages/Webform/Corporate/CorporateAddDirectorAddress"));
const CorporateAddDirectorBankDetails = lazy(() => import("../pages/Webform/Corporate/CorporateAddDirectorBankDetails"));
const CorporateWebformShareholderList = lazy(() => import("../pages/Webform/Corporate/CorporateWebformShareholderList"));
const CorporateAddShareholdePersonalDetails = lazy(() => import("../pages/Webform/Corporate/CorporateAddShareholdePersonalDetails"));
const CorporateAddShareholdeAddress = lazy(() => import("../pages/Webform/Corporate/CorporateAddShareholdeAddress"));
const CorporateAddShareholderBankDetails = lazy(() => import("../pages/Webform/Corporate/CorporateAddShareholderBankDetails"));
const CorporateWebformMembersList = lazy(() => import("../pages/Webform/Corporate/CorporateWebformMembersList"));
const CorporateWebformApplicationDone = lazy(() => import("../pages/Webform/Corporate/CorporateWebformApplicationDone"));
const AddCompanyShareholder = lazy(() => import("../pages/Profile/Corporate/ShareHolders/AddCompanyShareholder"));
const CorporateAddCompanyShareholder = lazy(() => import("../pages/Webform/Corporate/CorporateAddCompanyShareholder"));
const SecureDocumentUpload = lazy(() => import("../pages/SecureDocumentUpload/SecureDocumentUpload"));
const PaymentReceived = lazy(() => import("../pages/ViewClientBilling/PaymentReceived"));
const AddClientUser = lazy(() => import("../pages/ManageUsers/AddClientUser"));
const AddOomeroUser = lazy(() => import("../pages/ManageUsers/AddOomeroUser"));
const HistoryRate = lazy(() => import("../pages/FxRates/HistoryRate"));
const EmailMasters = lazy(() => import("../pages/EmailMasters/EmailMasters"));
const AddEmailMasters = lazy(() => import("../pages/EmailMasters/AddEmailMasters"));
const ViewEmailTemplate = lazy(() => import("../pages/EmailMasters/ViewEmailTemplate"));
const EditEmailTemplate = lazy(() => import("../pages/EmailMasters/EditEmailTemplate"));
import PrivateRoute from "./privateRoute";
const ListofClientUsers = lazy(() => import("../pages/ManageUsers/ListofClientUsers"));
const UserLists = lazy(() => import("../pages/ManageUsers/UserLists"));
const PreviouslyDirectorReports = lazy(() => import("../pages/CompanyCheck/PreviouslyDirectorReports"));
const CreateProfile = lazy(() => import("../pages/Profile/CreateProfile"));
const WebformThankYouPage = lazy(() => import("../pages/Webform/Individual/WebformThankYouPage"));
const IndividualPermission = lazy(() => import("../pages/Profile/Individual/IndividualPermission"));
const ProjectReport = lazy(() => import("../pages/ProjectReport/ProjectReport"));
const AmlCheck = lazy(() => import("../pages/AmlCheck/AmlCheck"));
const AmlCheckDetails = lazy(() => import("../pages/AmlCheck/AmlCheckDetails"));
const CompanyReport = lazy(() => import("../pages/CompanyReport/CompanyReport"));
const BankDetailsData = lazy(() => import("../components/CorporateReportComponents/BankDetailsData"));
const DataTable = lazy(() => import("../components/CorporateReportComponents/DataTable"));
const OnboardIndividualClient = lazy(() => import("../pages/Profile/OnboardUsingOomeroId/OnboardIndividualClient"));
const CompanyCheckReport = lazy(() => import("../pages/CompanyCheckReport/CompanyCheckReport"));
const PaymentHistory = lazy(() => import("../pages/MyAccount/PaymentHistory"));
const CorporateViewedScreening = lazy(() => import("../pages/ViewScreeningDetails/corporate/corporateViewedScreening"));
import TestScreen from "./TestScreen";
const WebNewClientSetup = lazy(() => import("../pages/webManageClients/WebNewClientSetup"));
const OnboardingInfo = lazy(() => import("../pages/webManageClients/OnboardingInfo"));
const SetUpNewPassword = lazy(() => import("../pages/webManageClients/SetUpNewPassword"));
const ManageNewClients = lazy(() => import("../pages/webManageClients/ManageNewClients"));
const PostpaidBilling = lazy(() => import("../pages/ViewClientBilling/PostpaidBilling"));
const ViewPostpaidBilling = lazy(() => import("../pages/ViewClientBilling/ViewPostpaidBilling"));
const WalletTable = lazy(() => import("../pages/WalletDetails/WalletTable"));
const SuperAdminWalletDashboard = lazy(() => import("../pages/WalletDetails/SuperAdminWalletDashboard"));
const GeographicRisk = lazy(() => import("../pages/GeographicRisk/GeographicRisk"));
const Faq = lazy(() => import("../pages/FAQ/Faq"));
const Help = lazy(() => import("../pages/Help/Help"));
const FaqManage = lazy(() => import("../pages/FAQ/FaqManage"));
const CustomPackageInquiry = lazy(() => import("../pages/CustomPackageInquiry/CustomPackageInquiry"));
const DeveloperServices = lazy(() => import("../pages/DeveloperServices/DeveloperServices"));
const KycAmlPricingAdmin = lazy(() => import("../pages/KycAmlPricingAdmin/KycAmlPricingAdmin"));
const WebsiteWalkthrough = lazy(() => import("../pages/WebsiteWalkthrough/WebsiteWalkthrough"));
const AdminCustomPackages = lazy(() => import("../pages/AdminCustomPackages/AdminCustomPackages"));
const RiskScoring = lazy(() => import("../pages/RiskScoring/RiskScoring"));
const AddOomeroCompany = lazy(() => import("../pages/OomeroCompany/AddOomeroCompany"));
const OomeroCompanyList = lazy(() => import("../pages/OomeroCompany/OomeroCompanyList"));
const SelfServiceSetup = lazy(() => import("../pages/OomeroCompany/SelfServiceSetup"));
const SelfServiceCountryLandingPage = lazy(() => import("../pages/OomeroCompany/SelfServiceCountryLandingPage"));
const FormTemplatesSetup = lazy(() => import("../pages/OomeroCompany/FormTemplatesSetup"));
const OnboardingPlanView = lazy(() => import("../pages/OomeroCompany/OnboardingPlanView"));
const AssignCountriesAdmin = lazy(() => import("../pages/OomeroCompany/AssignCountriesAdmin"));
const ServiceSlabsAdmin = lazy(() => import("../pages/OomeroCompany/ServiceSlabsAdmin"));
// import CompanyPage from "../pages/CompanyReport/CompanyPage";
const SwaggerPage = lazy(() => import("../pages/swagger/SwaggerPage"));
const DeveloperApi = lazy(() => import("../pages/DeveloperApi/DeveloperApi"));
const CrmSdk = lazy(() => import("../pages/CrmSdk/CrmSdk"));
const PropSpaceContract = lazy(() => import("../pages/PartnerContract/PropSpaceContract"));
const LoginNewsManage = lazy(() => import("../pages/LoginNews/LoginNewsManage"));
const PaymentGatewaySettings = lazy(() => import("../pages/Payments/PaymentGatewaySettings"));
const OfferCodes = lazy(() => import("../pages/OfferCodes/OfferCodes"));
const StrReports = lazy(() => import("../pages/StrReports/StrReports"));
const CrmRegisters = lazy(() => import("../pages/CrmRegisters/CrmRegisters"));
const ProcedureManual = lazy(() => import("../pages/ProcedureManual/ProcedureManual"));
const LoginHistory = lazy(() => import("../pages/LoginHistory/LoginHistory"));
const DeviceRecords = lazy(() => import("../pages/DeviceRecords/DeviceRecords"));
const StripePaymentReturn = lazy(() => import("../pages/Payments/StripePaymentReturn"));
const CompanyOverview = lazy(() => import('../pages/CompanyOverview/CompanyOverview'));
const OpenCasesByCompany = lazy(() => import('../pages/OpenCasesByCompany/OpenCasesByCompany'));
const DigiLockerSharedView = lazy(() => import('../pages/DigiLockerSharedView/DigiLockerSharedView'));
const DigiLockerComplete = lazy(() => import('../pages/DigiLockerComplete/DigiLockerComplete'));
const OomeroIdCountryChanges = lazy(() => import('../pages/OomeroIdCountryChanges/OomeroIdCountryChanges'));
// The separate log pages now open as one log on the Audit & Logs page.
const logRedirect = (log) => function LogRedirect() { return <Navigate to={`/logs?log=${log}`} replace />; };
const AuditorLogRedirect = logRedirect('auditor');
const UserLogRedirect = logRedirect('user');
const EmailLogRedirect = logRedirect('email');
const ConsentLogRedirect = logRedirect('consent');
export const loginRoutes = [
  {
    path: "/crm-sdk",
    exact: true,
    element: <CrmSdk />,
  },
  // Open like the CRM SDK page, so the contract link can go to PropSpace.
  {
    path: "/partners/propspace",
    exact: true,
    element: <PropSpaceContract />,
  },
  {
    path: "/digilocker/complete",
    exact: true,
    element: <DigiLockerComplete />,
  },
  {
    path: "/digilocker/share/:token",
    exact: true,
    element: <DigiLockerSharedView />,
  },
  {
    path: "/",
    exact: true,
    // A gold refinery on the refinery menu lands on its Refinery overview.
    element: <PrivateRoute component={DashboardHome} />,
  },
  {
    path: "/dashboard",
    exact: true,
    element: <PrivateRoute component={Dashboard} />,
  },
  {
    path: "/auditors_dashboard",
    exact: true,
    element: <PrivateRoute component={AuditorsDashboard} />,
  },
  {
    path: "/auditor_logs",
    exact: true,
    element: <PrivateRoute component={AuditorLogRedirect} />,
  },
  {
    path: "/login",
    exact: true,
    element: <Login />,
  },
  {
    path: "/find-oomero-id",
    exact: true,
    element: <FindOomeroId />,
  },
  {
    path: "/forgetpassword",
    exact: true,
    element: <ForgetPassword />,
  },
  {
    path: "/resetpassword",
    exact: true,
    element: <ResetPassword />,
  },
  {
    path: "/registration",
    exact: true,
    element: <Reigistration />,
  },
  {
    path: "/change_password",
    exact: true,
    element: <PrivateRoute component={ChangePassword} />,
  },
  {
    path: "/my_profile",
    exact: true,
    element: <PrivateRoute component={MyProfile} />,
  },
  {
    path: "/settings/client-rekyc-time",
    exact: true,
    element: (
      <PrivateRoute
        component={ClientRekycTime}
        requiredPermissions={["manage_client_rekyc_settings"]}
        allowEnterpriseAdmin
      />
    ),
  },
  {
    path: "/settings/aml-risk",
    exact: true,
    element: (
      <PrivateRoute
        component={ComplianceRiskSettings}
        requiredPermissions={["manage_client_rekyc_settings"]}
        allowEnterpriseAdmin
      />
    ),
  },
  {
    path: "/settings/risk-weightage",
    exact: true,
    element: <PrivateRoute component={RiskWeightageSettings} requiredPermissions={["manage_client_rekyc_settings"]} allowEnterpriseAdmin />,
  },
  {
    path: "/settings/compliance-risk",
    exact: true,
    element: (
      <PrivateRoute
        component={ComplianceRiskSettings}
        requiredPermissions={["manage_client_rekyc_settings"]}
        allowEnterpriseAdmin
      />
    ),
  },
  {
    path: "/settings/ubo-threshold",
    exact: true,
    element: (
      <PrivateRoute
        component={UboThreshold}
        requiredPermissions={["manage_client_rekyc_settings"]}
        allowEnterpriseAdmin
      />
    ),
  },
  {
    path: "/billing/self-service",
    exact: true,
    element: (
      <PrivateRoute
        component={SelfServicePlan}
        requiredPermissions={["manage_self_service_plan"]}
        allowEnterpriseAdmin
        allowOomero
      />
    ),
  },
  {
    path: "/welcome_screen",
    exact: true,
    element: <WelcomeScreen />,
  },
  {
    path: "/webform_personal_details",
    exact: true,
    element: <WebformPersonalDetails />,
  },
  {
    path: "/webform_address",
    exact: true,
    element: <WebformAddress />,
  },
  {
    path: "/webform_bank_details",
    exact: true,
    element: <WebformBankDetails />,
  },
  {
    path: "/webform_kyc_verification",
    exact: true,
    element: <WebformKYCVerification />,
  },
  {
    path: "/webform_appication_done",
    exact: true,
    element: <WebformAppicationDone />,
  },
  {
    path: "/webform_thank_you",
    exact: true,
    element: <WebformThankYouPage />,
  },
  {
    path: "/Corporate_welcome_screen",
    exact: true,
    element: <CorporateWelcomeScreen />,
  },
  {
    path: "/secure-document-upload",
    exact: true,
    element: <SecureDocumentUpload />,
  },
  {
    path: "/corporate_webform_information",
    exact: true,
    element: <CorporateWebformInformation />,
  },
  {
    path: "/corporate_webform_compliance",
    exact: true,
    element: <CorporateWebformCompliance />,
  },
  {
    path: "/corporate_webform_bankdetails",
    exact: true,
    element: <CorporateWebformBankDetails />,
  },
  {
    path: "/corporate_webform_directorlist",
    exact: true,
    element: <CorporateWebformDirectorList />,
  },
  {
    path: "/webform_add_director_personl_details",
    exact: true,
    element: <WebformAddDirectorPersonlDetails />,
  },
  {
    path: "/corporate_add_director_address",
    exact: true,
    element: <CorporateAddDirectorAddress />,
  },
  {
    path: "/corporate_add_director_bank_details",
    exact: true,
    element: <CorporateAddDirectorBankDetails />,
  },
  {
    path: "/corporate_webform_shareholder_list",
    exact: true,
    element: <CorporateWebformShareholderList />,
  },
  {
    path: "/corporate_add_shareholde_personal_details",
    exact: true,
    element: <CorporateAddShareholdePersonalDetails />,
  },
  {
    path: "/corporate_add_shareholde_address",
    exact: true,
    element: <CorporateAddShareholdeAddress />,
  },
  {
    path: "/CompanyOverview",
    exact: true,
    element: <PrivateRoute component={CompanyOverview} requiredPermissions={["view_company_overview", "manage_company_overview"]} allowEnterpriseAdmin />,
  },
  {
    path: "/company-overview",
    exact: true,
    element: <PrivateRoute component={CompanyOverview} requiredPermissions={["view_company_overview", "manage_company_overview"]} allowEnterpriseAdmin />,
  },
  {
    path: "/company_overview",
    exact: true,
    element: <PrivateRoute component={CompanyOverview} requiredPermissions={["view_company_overview", "manage_company_overview"]} allowEnterpriseAdmin />,
  },
  {
    path: "/corporate_add_shareholder_bank_details",
    exact: true,
    element: <CorporateAddShareholderBankDetails />,
  },
  {
    path: "/corporate_webform_mlro_details",
    exact: true,
    element: <CorporateWebformMlroDetails />,
  },
  {
    path: "/corporate_webform_members_list",
    exact: true,
    element: <CorporateWebformMembersList />,
  },
  {
    path: "/corporate_webform_application_done",
    exact: true,
    element: <CorporateWebformApplicationDone />,
  },
  {
    path: "/corporate_add_company_shareholder",
    exact: true,
    element: <CorporateAddCompanyShareholder />,
  },
  {
    path: "/verification/face-kyc",
    exact: true,
    element: <FaceVerification />,
  },
];
export const routes = [
  {
    path: "/reports",
    exact: true,
    element: <PrivateRoute component={ReportsCenter} />,
  },
  {
    path: "/tax-overview",
    exact: true,
    element: <PrivateRoute component={TaxOverview} />,
  },
  {
    path: "/user_log",
    exact: true,
    element: <PrivateRoute component={UserLogRedirect} requiredPermissions={["view_user_log"]} allowEnterpriseAdmin />,
  },
  {
    path: "/developer_services",
    exact: true,
    element: <PrivateRoute component={DeveloperServices} />,
  },
  {
    path: "/developer-api",
    exact: true,
    element: <PrivateRoute component={DeveloperApi} requiredPermissions={["view_developer_api"]} allowEnterpriseAdmin />,
  },
  {
    path: "/governance",
    exact: true,
    element: (
      <PrivateRoute
        component={Governance}
        requiredPermissions={["view_governance", "manage_governance", "manage_governance_purge"]}
        oomeroOnly
        allowOomero
      />
    ),
  },
  {
    path: "/workflow-builder",
    exact: true,
    element: <PrivateRoute component={WorkflowBuilder} requiredPermissions={["manage_workflows", "view_workflows"]} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/kyc-aml-pricing-admin",
    exact: true,
    element: <PrivateRoute component={KycAmlPricingAdmin} />,
  },
  {
    path: "/website-walkthrough",
    exact: true,
    element: <PrivateRoute component={WebsiteWalkthrough} />,
  },
  {
    path: "/projectreport",
    exact: true,
    element: <ProjectReport />,
  },

  {
    path: "/screening",
    exact: true,
    element: <Screening />,
  },
  {
    path: "/corporate_screening",
    exact: true,
    element: <PrivateRoute component={CorporateScreening} />,
  },
  {
    path: "/corporate_screening/screening",
    exact: true,
    element: <PrivateRoute component={StartScreening} />,
  },
  {
    path: "/view_corporate",
    exact: true,
    element: <PrivateRoute component={CorporateViewedScreening} />,
  },
  {
    path: "/view_corporate/corporate_screening_details",
    exact: true,
    element: <PrivateRoute component={StartScreening} />,
  },
  {
    path: "/create_profile",
    exact: true,
    element: <PrivateRoute component={CreateProfile} />
  },
  {
    path: "/webform/individual_personal_details",
    exact: true,
    element: <IndividualPersonalDetails />,
  },
  {
    path: "/webform/individual_address",
    exact: false,
    element: <IndividualAddress />,
  },
  {
    path: "/webform/individual_bank_details",
    exact: false,
    element: <IndividualBankDetails />,
  },
  {
    path: "/webform/individual_kyc_verification",
    exact: false,
    element: <IndividualKYCVerification />,
  },
  {
    path: "/webform/individual_application_done",
    exact: true,
    element: <IndividualAppicationDone />,
  },
  {
    path: "/profile/individual_personal_details",
    exact: true,
    element: <PrivateRoute component={IndividualPersonalDetails} />,
  },
  {
    path: "/profile/individual_address",
    exact: true,
    element: <PrivateRoute component={IndividualAddress} />,
  },
  {
    path: "/profile/individual_bank_details",
    exact: true,
    element: <PrivateRoute component={IndividualBankDetails} />,
  },
  {
    path: "/profile/individual_kyc_verification",
    exact: true,
    element: <IndividualKYCVerification />,
  },
  {
    path: "/profile/individual_kyc_waiting",
    exact: true,
    element: <PrivateRoute component={IndividualKYCWaiting} />,
  },
  {
    // This route handles Facia's redirect after KYC completion
    // Facia redirects to /kyc-waiting?entity_client_id=xxx
    path: "/kyc-waiting",
    exact: true,
    element: <IndividualKYCWaiting />,
  },
  {
    path: "/webform/individual_kyc_waiting",
    exact: true,
    element: <IndividualKYCWaiting />,
  },
  {
    path: "/profile/individual_application_done",
    exact: true,
    element: <PrivateRoute component={IndividualAppicationDone} />,
  },
  {
    path: "/individual_onboarding_web_welcome",
    exact: true,
    element: <IndividualWebWelcome />,
  },
  {
    path: "/individual_onboarding_web_permission",
    exact: true,
    element: <IndividualPermission />,
  },
  {
    path: "/individual_onboarding_web_personal_detail",
    exact: true,
    element: <IndividualWebPersonalDetails />,
  },
  {
    path: "/individual_onboarding_web_address",
    exact: true,
    element: <IndividualWebAddress />,
  },
  {
    path: "/individual_onboarding_web_bank_detail",
    exact: true,
    element: <IndividualWebBankDetail />,
  },
  {
    path: "/individual_onboarding_web_kyc_verification",
    exact: true,
    element: <IndividualWebKYCVerification />,
  },
  {
    path: "/individual_onboarding_web_application_done",
    exact: true,
    element: <IndividualWebApplication />,
  },
  {
    path: "/profile/corporate_company_details",
    exact: true,
    element: <PrivateRoute component={CorporatePersonalDetails} />,
  },
  {
    path: "/profile/corporate_company_information",
    exact: true,
    element: <PrivateRoute component={CorporateInformation} />,
  },
  {
    path: "/profile/corporate_company_compliance",
    exact: true,
    element: <PrivateRoute component={CorporateCompliance} />,
  },
  {
    path: "/profile/corporate_company_bank",
    exact: true,
    element: <PrivateRoute component={CorporateBankDetails} />,
  },
  {
    path: "/profile/corporate_company_director_list",
    exact: true,
    element: <PrivateRoute component={CorporateDirectorList} />,
  },
  {
    path: "/profile/director_personal_details",
    exact: true,
    element: <PrivateRoute component={AddDirectorPersonlDetails} />,
  },
  {
    path: "/profile/director_address",
    exact: true,
    element: <AddDirectorAddress />,
  },
  {
    path: "/profile/director_bank_details",
    exact: true,
    element: <AddDirectorBankDetails />,
  },
  {
    path: "/profile/corporate_company_shareholder_list",
    exact: true,
    element: <PrivateRoute component={CorporateShareholderList} />,
  },
  {
    path: "/profile/shareholder_personal_details",
    exact: true,
    element: <AddShareholdePersonalDetails />,
  },
  {
    path: "/profile/shareholder_address",
    exact: true,
    element: <AddShareholdeAddress />,
  },
  {
    path: "/profile/shareholder_bank_details",
    exact: true,
    element: <AddShareholderBankDetails />,
  },
  {
    path: "/profile/add_company_shareholder",
    exact: true,
    element: <PrivateRoute component={AddCompanyShareholder} />,
  },
  {
    path: "/profile/corporate_mlro_details",
    exact: true,
    element: <PrivateRoute component={CorporateMlroDetails} />,
  },
  {
    path: "/profile/corporate_members_list",
    exact: true,
    element: <PrivateRoute component={CorporateMembersList} />,
  },
  {
    path: "/profile/corporate_application",
    exact: true,
    element: <PrivateRoute component={CorporateApplicationDone} />,
  },
  {
    path: "/manage_clients",
    exact: true,
    element: <PrivateRoute component={ManageClients} />,
  },
  {
    path: "/clients/manage_New_client",
    exact: true,
    element: <NewClientSetup />,
  },
  {
    path: "/clients/manage_New_client_services",
    exact: true,
    element: <ClientServiceSetup />,
  },
  {
    path: "/clients/manage_admin",
    exact: true,
    element: <ManageAdmin />,
  },
  {
    path: "/aml_check",
    exact: true,
    element: <SearchAmlCheck />,
  },
  {
    path: "/aml/search_results",
    exact: true,
    element: <ResultsSearch />,
  },
  {
    path: "/aml/search_data",
    exact: true,
    element: <SingleResultsData />,
  },
  {
    path: "/user_roles",
    exact: true,
    element: <PrivateRoute component={UsersRole} />,
  },
  {
    path: "/user/add_user_role",
    exact: true,
    element: <PrivateRoute component={AddNewUserRoles} />,
  },
  {
    path: "/tax",
    exact: true,
    element: <PrivateRoute component={Tax} />,
  },
  {
    path: "/tax/addtax",
    exact: true,
    element: <PrivateRoute component={Addtax} />,
  },
  {
    path: "/tax/editTax",
    exact: true,
    element: <PrivateRoute component={Addtax} />,
  },
  {
    path: "/fxrates",
    exact: true,
    element: <PrivateRoute component={FxRates} />,
  },
  {
    path: "/email_masters",
    exact: true,
    element: <PrivateRoute component={EmailMasters} requiredPermissions={["view_email_master", "view_email_master_list"]} />,
  },
  {
    path: "/email_history",
    exact: true,
    element: <PrivateRoute component={EmailLogRedirect} requiredPermissions={["view_email_log", "view_email_master", "view_email_master_list"]} allowEnterpriseAdmin />,
  },
  {
    path: "/settings/global-roles",
    exact: true,
    element: <PrivateRoute component={() => <UsersRole globalMode />} oomeroOnly />,
  },
  {
    path: "/settings/global-roles/edit",
    exact: true,
    element: <PrivateRoute component={() => <AddNewUserRoles globalMode />} oomeroOnly />,
  },
  {
    path: "/consent_log",
    exact: true,
    element: <PrivateRoute component={ConsentLogRedirect} requiredPermissions={["view_consent_log"]} allowEnterpriseAdmin />,
  },
  {
    path: "/email_masters/add_email_masters",
    exact: true,
    element: <PrivateRoute component={AddEmailMasters} requiredPermissions={["view_email_master", "view_email_master_list"]} />,
  },
  {
    path: "/email_masters/view_email_template",
    exact: true,
    element: <PrivateRoute component={ViewEmailTemplate} requiredPermissions={["view_email_master", "view_email_master_list"]} />,
  },
  {
    path: "/email_masters/edit_email_template",
    exact: true,
    element: <PrivateRoute component={EditEmailTemplate} requiredPermissions={["view_email_master", "view_email_master_list"]} />,
  },
  {
    path: "/rates/manage_fxrates",
    exact: true,
    element: <PrivateRoute component={AddNewFxRates} />,
  },
  {
    path: "/rates/history_rates",
    exact: true,
    element: <PrivateRoute component={HistoryRate} />,
  },
  {
    path: "/client_biling",
    exact: true,
    element: <PrivateRoute component={ClientBiling} />,
  },
  {
    path: "/billing",
    exact: true,
    element: <PrivateRoute component={PostpaidBilling} />,
  },
  {
    path: "/billing/view_client_biling",
    exact: true,
    element: <PrivateRoute component={ViewClientBilling} />,
  },
  {
    path: "/billing/view_postpaid_billing",
    exact: true,
    element: <PrivateRoute component={ViewPostpaidBilling} />,
  },
  {
    path: "/billing/previous_bills",
    exact: true,
    element: <PrivateRoute component={PreviousBills} />,
  },
  {
    path: "/billing/edit_pricing",
    exact: true,
    element: <EditPricing />,
  },
  {
    path: "/billing/payment_received",
    exact: true,
    element: <PrivateRoute component={PaymentReceived} />,
  },
  {
    path: "/manage_users",
    exact: true,
    element: <PrivateRoute component={ManageUsers} />,
  },
  {
    path: "users/list_of_oomero_users",
    exact: true,
    element: <PrivateRoute component={UserLists} />,
  },
  {
    path: "users/list_of_client_users",
    exact: true,
    element: <PrivateRoute component={UserLists} />,
  },
  {
    path: "users/add_new_users",
    exact: true,
    element: <AddNewUsers />,
  },
  {
    path: "/users/add_client_users",
    exact: true,
    element: <PrivateRoute component={AddClientUser} />,
  },
  {
    path: "/user/edit_client_user",
    exact: true,
    element: <PrivateRoute component={AddClientUser} />,
  },
  {
    path: "/users/add_oomero_users",
    exact: true,
    element: <PrivateRoute component={AddOomeroUser} />,
  },
  {
    path: "/user/edit_oomero_user",
    exact: true,
    element: <PrivateRoute component={AddOomeroUser} />,
  },
  {
    path: "/company_check",
    exact: true,
    element: <PrivateRoute component={CompanyCheck} />,
  },
  {
    // path: "/company/previously_reports",
    // exact: true,
    // element: <PreviouslyReports />,
  },
  {
    path: "/company/previously_reports",
    exact: true,
    element: < PrivateRoute component={PreviouslyDirectorReports} />,
  },
  {
    path: "/corporate_screening/corporate_screening_details",
    exact: true,
    element: <PrivateRoute component={ScreeningCompanyDetails} />,
  },
  {
    path: "/corporate_screening/company_shareholder_screening_details",
    exact: true,
    element: <PrivateRoute component={CompanyShareholderScreeningDetails} />,
  },
  {
    path: "/individual_screening",
    exact: true,
    element: <PrivateRoute component={IndividualScreening} />,
  },
  {
    path: "/open-cases-by-company",
    exact: true,
    element: <PrivateRoute component={OpenCasesByCompany} />,
  },
  {
    path: "/individual_screening/individual_screening_details",
    exact: true,
    element: <PrivateRoute component={IndividualScreeningDetails} />,
  },
  {
    path: "/view_individual_user",
    exact: true,
    element: <PrivateRoute component={IndividualViewedScreening} />,
  },
  {
    path: "/view_individual_user/individual_screening_details",
    exact: true,
    element: <PrivateRoute component={IndividualScreeningDetails} />,
  },
  {
    path: "/individual_screening/client_individual_screening_details",
    exact: true,
    element: <PrivateRoute component={IndividualScreeningDetails} />,
  },
  {
    path: "/view_screening/corporate_list",
    exact: true,
    element: <ViewAllCorporateList />,
  },
  {
    path: "/view_screening/individual_list",
    exact: true,
    element: <ViewAllIndividualList />,
  },
  {
    path: "/pricing",
    exact: true,
    element: <Pricing />,
  },
  {
    path: "/projectreport",
    exact: true,
    element: <ProjectReport />,
  },
  {
    path: "/companyreport",
    exact: true,
    element: <CompanyReport />,
  },
  {
    path: "/invoices",
    exact: true,
    element: <PrivateRoute component={Invoices} />,
  },
  {
    path: "/active_plan",
    exact: true,
    element: <PrivateRoute component={ActivePlan} />,
  },
  // {
  //   path: "/keydetails",
  //   exact: true,
  //   element: <KeyDetailsComponent />,
  // },
  {
    path: "/bankdata",
    exact: true,
    element: <BankDetailsData />,
  },
  {
    path: "/datatable",
    exact: true,
    element: <DataTable />,
  },
  {
    path: "/webnew_client_setup",
    exact: true,
    element: <PrivateRoute component={WebNewClientSetup} />,
  },
  {
    path: "/google_authenticator",
    exact: true,
    element: <PrivateRoute component={GoogleAuthenticator} />,
  },
  {
    path: "/aml-check",
    exact: true,
    element: <PrivateRoute component={AmlCheck} />,
  },
  {
    path: "/aml-check/aml_details",
    exact: true,
    element: <PrivateRoute component={AmlCheckDetails} />,
  },
  // AML Check on its own (6 Oct 2026): the same pages without the main menu.
  {
    path: "/aml",
    exact: true,
    element: <PrivateRoute component={AmlCheck} layout="aml-workspace" />,
  },
  {
    path: "/aml/check",
    exact: true,
    element: <PrivateRoute component={AmlCheckDetails} layout="aml-workspace" />,
  },
  {
    path: "/onboard_with_oomero_id",
    exact: true,
    element: <PrivateRoute component={OnboardIndividualClient} />,
  },
  {
    path: "/company_aml_check_report",
    exact: true,
    element: <CompanyCheckReport />,
  },
  // {
  //   path: "/company_page_report",
  //   exact: true,
  //   element: <CompanyPage/>,
  // },
  {
    path: "/payment_history",
    exact: true,
    element: <PrivateRoute component={PaymentHistory} />,
  },

  {
    path: "/onboarding_info",
    exact: true,
    element: <PrivateRoute component={OnboardingInfo} />,
  },

  {
    path: "/manage_new_client",
    exact: true,
    element: <PrivateRoute component={ManageNewClients} oomeroOnly requiredPermissions={["setup_new_client", "view_client_list"]} />,
  },

  {
    path: "/risk_scoring",
    exact: true,
    element: <PrivateRoute component={RiskScoring} />,
  },
  {
    path: "/test_screen",
    exact: true,
    element: <PrivateRoute component={TestScreen} />,
  },
  {
    path: "/wallet_details",
    exact: true,
    element: <PrivateRoute component={WalletTable} />,
  },
  {
    path: "/wallet-dashboard",
    exact: true,
    element: <PrivateRoute component={SuperAdminWalletDashboard} />,
  },
  {
    path: "/setup_password",
    exact: true,
    element: <SetUpNewPassword />

  },

  {
    path: "/test_screen",
    exact: true,
    element: <PrivateRoute component={TestScreen} />,
  },
  {
    path: "/geographic_risk",
    exact: true,
    element: <PrivateRoute component={GeographicRisk} />,
  },
  {
    path: "/help",
    exact: true,
    element: <PrivateRoute component={Help} />,
  },
  {
    path: "/faq",
    exact: true,
    element: <PrivateRoute component={Faq} />,
  },
  {
    path: "/faq/manage",
    exact: true,
    element: <PrivateRoute component={FaqManage} requiredPermissions={["manage_faqs"]} />,
  },
  {
    path: "/login-news/manage",
    exact: true,
    element: <PrivateRoute component={LoginNewsManage} superAdminOnly />,
  },
  {
    path: "/settings/payment-gateway",
    exact: true,
    element: <PrivateRoute component={PaymentGatewaySettings} oomeroOnly />,
  },
  {
    // Offer codes for special company packages (Trello #270).
    path: "/settings/offer-codes",
    exact: true,
    element: <PrivateRoute component={OfferCodes} oomeroOnly />,
  },
  {
    // SAR / STR reporting (Trello #271): every report of the company, with the approval matrix.
    path: "/crm/str-reports",
    exact: true,
    element: <PrivateRoute component={StrReports} />,
  },
  {
    // CRM registers: PEP register, UBO master list, annual AML return (Trello #274).
    path: "/crm/registers",
    exact: true,
    element: <PrivateRoute component={CrmRegisters} />,
  },
  {
    // The compliance dashboard (Trello #272) is a tab of the main dashboard since the 11 Oct 2026
    // meeting; the old address opens it there.
    path: "/crm/dashboard",
    exact: true,
    element: <Navigate to="/?view=compliance" replace />,
  },
  {
    // The procedure manual for compliance officers and the MLRO (Trello #273).
    path: "/help/procedure-manual",
    exact: true,
    element: <PrivateRoute component={ProcedureManual} />,
  },
  {
    // Every sign-in with its device; super admins only (14 Oct 2026).
    path: "/login-history",
    exact: true,
    element: <PrivateRoute component={LoginHistory} superAdminOnly />,
  },
  {
    // Every device stored for the screening profiles; super admins only (14 Oct 2026).
    path: "/device-records",
    exact: true,
    element: <PrivateRoute component={DeviceRecords} superAdminOnly />,
  },
  {
    // Stripe Checkout sends the customer back here after paying (or cancelling).
    path: "/payment/stripe/return",
    exact: true,
    element: <StripePaymentReturn />,
  },
  {
    path: "/company_tax_mapping",
    exact: true,
    element: <PrivateRoute component={CompanyTaxMapping} />,
  },
  {
    path: "/custom-package-inquiry",
    exact: true,
    element: <CustomPackageInquiry />,
  },
  {
    path: "/admin/custom-packages",
    exact: true,
    element: <PrivateRoute component={AdminCustomPackages} />,
  },
  {
    path: "/pending_biometrics",
    exact: true,
    element: <PrivateRoute component={IndividualScreening} />,
  },
  {
    path: "/pending_application",
    exact: true,
    element: <PrivateRoute component={IndividualScreening} />,
  },
  {
    path: "/admin/oomero-id-country-changes",
    exact: true,
    element: <PrivateRoute component={OomeroIdCountryChanges} superAdminOnly />,
  },
  {
    path: "/re_kyc",
    exact: true,
    element: <PrivateRoute component={IndividualScreening} />,
  },
  {
    path: "/comprehensive-report",
    exact: true,
    element: <PrivateRoute component={ComprehensiveReport} />,
  },
  {
    path: "/add_oomero_company",
    exact: true,
    element: <PrivateRoute component={AddOomeroCompany} oomeroOnly requiredPermissions={["setup_new_client"]} />,
  },
  {
    path: "/self_service_admin",
    exact: true,
    element: <PrivateRoute component={SelfServiceSetup} />,
  },
  {
    path: "/self_service_setup",
    exact: true,
    element: <PrivateRoute component={SelfServiceSetup} />,
  },
  {
    path: "/self_service_assign_countries",
    exact: true,
    element: <PrivateRoute component={AssignCountriesAdmin} superAdminOnly />,
  },
  {
    path: "/self_service_service_slabs",
    exact: true,
    element: <PrivateRoute component={ServiceSlabsAdmin} oomeroOnly requiredPermissions={["manage_service_slabs"]} />,
  },
  {
    path: "/self_service_country_landing",
    exact: true,
    element: <PrivateRoute component={SelfServiceCountryLandingPage} />,
  },
  {
    path: "/form_templates_setup",
    exact: true,
    element: <PrivateRoute component={FormTemplatesSetup} oomeroEnterpriseAdminOnly />,
  },
  {
    path: "/self_service_plan_view/:planId",
    exact: true,
    element: <PrivateRoute component={OnboardingPlanView} />,
  },
  {
    path: "/oomero_companies",
    exact: true,
    element: <PrivateRoute component={OomeroCompanyList} oomeroOnly requiredPermissions={["view_client_list"]} />,
  },
  {
    path: "/api-docs",
    exact: true,
    element: <SwaggerPage />,
  },
  {
    path: "/logs",
    exact: true,
    element: <PrivateRoute component={LogsHub} />,
  },
  {
    // Old address of the KYP checklist settings, now in the Refinery workspace.
    path: "/settings/kyp-checklist",
    exact: true,
    element: <PrivateRoute component={refinerySettingsRedirect("kyp")} requiredPermissions={["manage_client_rekyc_settings"]} allowEnterpriseAdmin />,
  },
  {
    // The customer's copy of a KYP checklist, opened from the emailed link (no login).
    path: "/kyp-checklist",
    exact: true,
    element: <KypCustomerChecklist />,
  },
  {
    // Old address of the client review settings, now in the Refinery workspace.
    path: "/settings/client-reviews",
    exact: true,
    element: <PrivateRoute component={refinerySettingsRedirect("reviews")} requiredPermissions={["manage_client_rekyc_settings"]} allowEnterpriseAdmin />,
  },
  {
    // The director's review form, opened from the emailed link (no login).
    path: "/client-review",
    exact: true,
    element: <ClientReviewForm />,
  },
  {
    // Old address of the documents checklist settings, now in the Refinery workspace.
    path: "/settings/document-checklist",
    exact: true,
    element: <PrivateRoute component={refinerySettingsRedirect("documents")} requiredPermissions={["manage_client_rekyc_settings"]} allowEnterpriseAdmin />,
  },
  {
    // The customer's upload page for requested checklist documents (no login).
    path: "/document-checklist",
    exact: true,
    element: <ChecklistUpload />,
  },
  {
    // Old address of the supply chain settings, now in the Refinery workspace.
    path: "/settings/supply-chain",
    exact: true,
    element: <PrivateRoute component={refinerySettingsRedirect("supply-chain")} requiredPermissions={["manage_client_rekyc_settings"]} allowEnterpriseAdmin />,
  },
  {
    // The customer's supply chain declaration, opened from the emailed link (no login).
    path: "/supply-chain",
    exact: true,
    element: <SupplyChainForm />,
  },
  {
    // Old address of the shipment document settings, now in the Refinery workspace.
    path: "/settings/shipment-documents",
    exact: true,
    element: <PrivateRoute component={refinerySettingsRedirect("shipments")} requiredPermissions={["manage_client_rekyc_settings"]} allowEnterpriseAdmin />,
  },
  {
    // The customer's shipment page: details, documents and suppliers (no login).
    path: "/shipment",
    exact: true,
    element: <ShipmentUpload />,
  },
  {
    // The customer's refinery onboarding form, opened from the emailed link (no login).
    path: "/refinery-onboarding",
    exact: true,
    element: <RefineryOnboardingPublic />,
  },
  // Refinery workspace (overview, clients, shipments, one company or customer, settings).
  {
    path: "/refinery",
    exact: true,
    element: <PrivateRoute component={RefineryHome} requiredPermissions={REFINERY_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    // Onboard a refinery customer, laid out like Create Profile.
    path: "/refinery/onboard",
    exact: true,
    element: <PrivateRoute component={RefineryOnboard} requiredPermissions={["create_business_onboarding"]} allowEnterpriseAdmin />,
  },
  {
    // The onboarding dashboard: every company's onboarding form and where it stands.
    path: "/refinery/onboardings",
    exact: true,
    element: <PrivateRoute component={RefineryOnboardings} requiredPermissions={REFINERY_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/refinery/clients",
    exact: true,
    element: <PrivateRoute component={RefineryClients} requiredPermissions={REFINERY_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/refinery/shipments",
    exact: true,
    element: <PrivateRoute component={RefineryShipments} requiredPermissions={REFINERY_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/refinery/company/:corporateClientId",
    exact: true,
    element: <PrivateRoute component={RefineryCompany} requiredPermissions={REFINERY_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/refinery/individual/:entityClientId",
    exact: true,
    element: <PrivateRoute component={RefineryIndividual} requiredPermissions={REFINERY_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/refinery/settings",
    exact: true,
    element: <PrivateRoute component={RefinerySettings} requiredPermissions={["manage_client_rekyc_settings"]} allowEnterpriseAdmin />,
  },
  {
    // Anyone can check an Oomero Asset ID (no login).
    path: "/verify-asset",
    exact: true,
    element: <VerifyAsset />,
  },
  // Oomero Asset IDs (list, add, issuer application, Oomero's reviews, one asset).
  {
    path: "/assets",
    exact: true,
    element: <PrivateRoute component={AssetsHome} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/new",
    exact: true,
    element: <PrivateRoute component={AssetNew} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/issuer",
    exact: true,
    element: <PrivateRoute component={AssetIssuer} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/issuers",
    exact: true,
    element: <PrivateRoute component={AssetIssuerReviews} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  // The Oomero Asset ID register (an account's own valuables). Prices is for Oomero only (checked by the server).
  {
    path: "/assets/register",
    exact: true,
    element: <PrivateRoute component={RegisterHome} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/register/new",
    exact: true,
    element: <PrivateRoute component={RegisterForm} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/register/:code",
    exact: true,
    element: <PrivateRoute component={RegisterItem} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/register/:code/edit",
    exact: true,
    element: <PrivateRoute component={RegisterForm} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/sales",
    exact: true,
    element: <PrivateRoute component={RegisterSales} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/plan",
    exact: true,
    element: <PrivateRoute component={RegisterPlan} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/prices",
    exact: true,
    element: <PrivateRoute component={RegisterPrices} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    path: "/assets/:code",
    exact: true,
    element: <PrivateRoute component={AssetDetail} requiredPermissions={ASSET_PERMISSIONS} allowEnterpriseAdmin allowOomero />,
  },
  {
    // Client book: the whole client list report (Trello "Client List report").
    path: "/reports/client-book",
    exact: true,
    element: (
      <PrivateRoute
        component={ClientBook}
        requiredPermissions={["view_onboarded_individual_profiles", "view_onboarded_corporate_profiles", "auditors_check_onboarded_individuals", "auditors_check_onboarded_corporates", "view_report_log"]}
        allowEnterpriseAdmin
      />
    ),
  },
];
